import { useEffect, useRef } from 'react';

/**
 * Relit une donnee quand l'onglet reprend la main (ADR 0035 §4).
 *
 * Aucun ecran d'administration ne se recharge seul : chaque lecture `/admin/*`
 * ecrit une ligne d'audit (ADR 0032), une minuterie noierait le registre. La
 * relecture au retour sur l'onglet, plus un bouton « Actualiser », suffisent a
 * l'exploitation.
 *
 * Deux evenements couvrent le retour — `focus` (fenetre) et `visibilitychange`
 * (onglet) — et **ils partent souvent ensemble** : sans garde, un seul retour
 * ecrirait deux lignes d'audit. `minimumIntervalMs` les fusionne.
 */
export const useRefreshOnFocus = (refresh: () => void, minimumIntervalMs = 5000): void => {
  const refreshRef = useRef(refresh);
  const lastRef = useRef(0);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    // L'ecran vient de charger : un `focus` dans les secondes qui suivent ne
    // doit pas le relire.
    lastRef.current = Date.now();

    const onReturn = (): void => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      const now = Date.now();

      if (now - lastRef.current < minimumIntervalMs) {
        return;
      }

      lastRef.current = now;
      refreshRef.current();
    };

    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);

    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [minimumIntervalMs]);
};
